import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import BoltIconActive from "@/assets/icons/bolt/filled/regular.svg?react"
import BoltIcon from "@/assets/icons/bolt/medium.svg?react"
import BuildingCommunityIconActive from "@/assets/icons/buildingCommunity/bold.svg?react"
import BuildingCommunityIcon from "@/assets/icons/buildingCommunity/medium.svg?react"
import ChartIconActive from "@/assets/icons/chart/bold.svg?react"
import ChartIcon from "@/assets/icons/chart/medium.svg?react"
import CloudIconActive from "@/assets/icons/cloud/filled/regular.svg?react"
import CloudIcon from "@/assets/icons/cloud/medium.svg?react"
import DashboardIconActive from "@/assets/icons/dashboard/filled/regular.svg?react"
import DashboardIcon from "@/assets/icons/dashboard/medium.svg?react"
import EmissionsIconActive from "@/assets/icons/emissions/filled/regular.svg?react"
import EmissionsIcon from "@/assets/icons/emissions/medium.svg?react"
import GalleryIconActive from "@/assets/icons/gallery/medium.svg?react"
import GalleryIcon from "@/assets/icons/gallery/medium.svg?react"
import ListScanIconActive from "@/assets/icons/listScan/medium.svg?react"
import ListScanIcon from "@/assets/icons/listScan/medium.svg?react"
import MobileIconActive from "@/assets/icons/mobile/filled/bold.svg?react"
import MobileIcon from "@/assets/icons/mobile/medium.svg?react"
import PlanetIcon from "@/assets/icons/planet/medium.svg?react"
import PlanetLeafIconActive from "@/assets/icons/planetLeaf/medium.svg?react"
import PlanetLeafIcon from "@/assets/icons/planetLeaf/medium.svg?react"
import ReportIconActive from "@/assets/icons/report/filled/bold.svg?react"
import ReportIcon from "@/assets/icons/report/medium.svg?react"
import SearchIconActive from "@/assets/icons/search/bold.svg?react"
import SearchIcon from "@/assets/icons/search/medium.svg?react"
import SkyscraperIconActive from "@/assets/icons/skyscraper/filled/bold.svg?react"
import SkyscraperIcon from "@/assets/icons/skyscraper/medium.svg?react"
import SpeakerIconActive from "@/assets/icons/speaker/medium.svg?react"
import SpeakerIcon from "@/assets/icons/speaker/medium.svg?react"
import { useEntityType } from "@/hooks/useEntityType"
import { useScope1Blocked } from "@/hooks/useScope1Blocked"
import { TNavbarNode } from "@/pages/dashboard/components/Navbar/utils"
import routes from "@/routes/routes"

export type TNavbarSection = {
  heading: string
  /** Shorter heading shown in the collapsed rail when the full heading is too long to wrap cleanly
   * in the narrow 80px width. Falls back to `heading` when omitted. */
  collapsedHeading?: string
  children: TNavbarNode[]
}

export default function useRoutes(): TNavbarSection[] {
  const { t } = useTranslation()
  const entityType = useEntityType()
  const { isBlocked: scope1Blocked } = useScope1Blocked()

  return useMemo(() => {
    const isCompany = entityType === "company"
    const isMunicipality = entityType === "municipality"
    // The Climate Overview, Scope 2 and Accounting get the Scope 1 lock while climate setup is not
    // completed; municipalities keep their current sidebar (MPE-1537).
    const climateSetupLocked = scope1Blocked && !isMunicipality

    const baseSections = [
      {
        heading: t("home"),
        children: [
          {
            heading: t("overview"),
            icon: { Base: DashboardIcon, Active: DashboardIconActive },
            href: routes.dashboard,
            isIndex: true,
            // The same page as the Climate Overview below: hidden (with its Home section) until the
            // Home redesign (MPE-1536). Municipalities keep it (MPE-1537).
            hidden: !isMunicipality,
          },
        ],
      },
      {
        heading: t("advertising"),
        children: [
          {
            heading: t("searchScreens"),
            icon: { Base: SearchIcon, Active: SearchIconActive },
            href: routes["dashboard/campaign-manager"],
            isIndex: true,
          },
          {
            heading: t("dashboard.campaigns.title"),
            icon: { Base: SpeakerIcon, Active: SpeakerIconActive },
            href: routes["dashboard/campaigns"],
            children: [
              {
                heading: t("dashboard.campaigns.reservations"),
                href: routes["dashboard/campaigns"],
              },
              {
                heading: t("dashboard.campaigns.moogliApp"),
                href: routes["dashboard/campaign-manager/app"],
                // Not built yet: hidden, not locked (show only what is built, MPE-1447).
                hidden: true,
              },
              {
                heading: t("dashboard.analytics"),
                href: routes["dashboard/analytics"],
                hidden: true,
              },
            ],
          },
          {
            heading: t("dashboard.contentStudio"),
            icon: { Base: GalleryIcon, Active: GalleryIconActive },
            href: routes["dashboard/content-studio"],
          },
        ],
      },
      {
        heading: t("organizationSetup.climate"),
        children: [
          {
            heading: t("organizationSetup.climateTitle"),
            icon: { Base: PlanetIcon, Active: PlanetIcon },
            href: routes.climateSetup,
          },
          {
            heading: t("overview"),
            icon: { Base: DashboardIcon, Active: DashboardIconActive },
            href: routes.climate,
            isIndex: true,
            // Before setup its page is the same checklist as Climate setup above, so it is locked instead.
            disabled: climateSetupLocked,
          },
        ],
      },
      {
        heading: isCompany ? t("company") : t("municipality"),
        children: [
          {
            heading: t("details"),
            icon: { Base: SkyscraperIcon, Active: SkyscraperIconActive },
            // Municipalities use the same organisation pages, under their own labels.
            href: routes["dashboard/company"],
            children: [
              {
                heading: isCompany ? t("parentCompany") : t("municipality"),
                href: routes["dashboard/company"],
                isIndex: true,
              },
              isCompany && {
                heading: t("reportingYear"),
                href: routes["dashboard/company/reporting-year"],
              },
              {
                heading: t("relatedEntities"),
                href: routes["dashboard/company/relatedEntities"],
              },
              {
                heading: t("locations"),
                href: routes["dashboard/company/locations"],
              },
            ],
          },
        ].filter(Boolean),
      } as TNavbarSection,
      {
        heading: t("emissions"),
        children: [
          {
            heading: t("scope1box"),
            icon: { Base: EmissionsIcon, Active: EmissionsIconActive },
            href: routes["dashboard/scope1"],
            disabled: scope1Blocked,
            children: [
              {
                heading: t("overview"),
                href: routes["dashboard/scope1"],
                isIndex: true,
              },
              {
                heading: t("stationaryCombustion"),
                href: routes["dashboard/scope1/stationaryCombustion"],
              },
              {
                heading: t("mobileCombustion"),
                href: routes["dashboard/scope1/mobileCombustion"],
              },
              {
                heading: t("fugitiveCombustion"),
                href: routes["dashboard/scope1/fugitiveCombustion"],
              },
            ],
          },
          {
            heading: t("scope2.title"),
            icon: { Base: BoltIcon, Active: BoltIconActive },
            href: routes["dashboard/scope2"],
            disabled: climateSetupLocked,
            children: [
              {
                heading: t("purchasedEnergy"),
                href: routes["dashboard/scope2"],
              },
              {
                heading: "EACs",
                href: routes["dashboard/scope2/eac"],
              },
              // {
              //   heading: "Renewable Generation",
              //   href: routes["dashboard/scope2/re-gen"],
              // },
            ],
          },
          {
            heading: t("scope3box"),
            icon: { Base: CloudIcon, Active: CloudIconActive },
            href: routes["dashboard/scope3"],
            disabled: true,
            children: [
              {
                heading: t("overview"),
                href: routes["dashboard/scope3"],
                isIndex: true,
              },
              {
                heading: t("upstreamActivities"),
                href: routes["dashboard/scope3/upstreamActivities"],
              },
              {
                heading: t("downstreamActivities"),
                href: routes["dashboard/scope3/downstreamActivities"],
              },
            ],
          },
        ],
      },
      {
        heading: t("ghgInventory"),
        collapsedHeading: t("ghgInventoryShort"),
        children: [
          {
            heading: t("results"),
            icon: { Base: ChartIcon, Active: ChartIconActive },
            href: routes["dashboard/results"],
          },
          {
            heading: t("accounting"),
            icon: { Base: ListScanIcon, Active: ListScanIconActive },
            href: routes["dashboard/accounting"],
            disabled: climateSetupLocked,
          },
          {
            heading: t("reports"),
            icon: { Base: ReportIcon, Active: ReportIconActive },
            href: routes["dashboard/reports"],
            // Sent users off to concon.moogli.io, a separate product, with no
            // warning that they were leaving. Disabled until reporting lands
            // here — the same treatment the other not-yet-built entries get.
            disabled: true,
          },
        ],
      },
      ...(isMunicipality
        ? [
            {
              heading: t("community"),
              // Locked until the Community pages exist (MPE-988).
              children: [
                {
                  heading: t("directory"),
                  icon: { Base: BuildingCommunityIcon, Active: BuildingCommunityIconActive },
                  href: routes["dashboard/municipality/directory"],
                  disabled: true,
                },
                {
                  heading: t("progress"),
                  icon: { Base: ChartIcon, Active: ChartIconActive },
                  href: routes["dashboard/municipality/progress"],
                  disabled: true,
                },
                {
                  heading: t("appAdoption"),
                  icon: { Base: MobileIcon, Active: MobileIconActive },
                  href: routes["dashboard/municipality/appAdoption"],
                  disabled: true,
                },
              ],
            },
          ]
        : []),
      {
        heading: t("reductionPlans"),
        collapsedHeading: t("reductionPlansShort"),
        children: [
          {
            heading: t("reductionPlan"),
            icon: { Base: PlanetLeafIcon, Active: PlanetLeafIconActive },
            href: routes["dashboard/reductionPlan"],
            disabled: true,
          },
        ],
      },
      {
        heading: t("impact"),
        children: [
          {
            heading: t("climateWebsite"),
            icon: { Base: PlanetIcon, Active: PlanetIcon },
            href: routes["dashboard/climateWebsite"],
            disabled: true,
          },
        ],
      },
    ]

    return baseSections
  }, [t, entityType, scope1Blocked])
}
